import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import CareerStrengthsCard from './components/CareerStrengthsCard';
import SkillGapAnalysis from './components/SkillGapAnalysis';
import JobRecommendationCard from './components/JobRecommendationCard';
import CareerProgressChart from './components/CareerProgressChart';
import ActionPlanCard from './components/ActionPlanCard';
import ReportExportCard from './components/ReportExportCard';

const CareerReport = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [bookmarkedJobs, setBookmarkedJobs] = useState(new Set());

  // Mock data for career report
  const reportData = {
    user: {
      name: "John Doe",
      assessmentDate: "2025-08-28",
      overallScore: 87
    },
    strengths: [
      {
        title: "Technical Leadership",
        description: "Strong ability to guide technical teams and make architectural decisions",
        score: 92
      },
      {
        title: "Problem Solving",
        description: "Excellent analytical skills and creative approach to complex challenges",
        score: 88
      },
      {
        title: "Communication",
        description: "Clear and effective communication with both technical and non-technical stakeholders",
        score: 85
      },
      {
        title: "Project Management",
        description: "Proven track record of delivering projects on time and within scope",
        score: 82
      }
    ],
    skillGaps: [
      {
        skill: "Cloud Architecture (AWS/Azure)",
        description: "Advanced cloud infrastructure design and implementation skills needed for senior roles",
        currentLevel: 65,
        targetLevel: 90,
        priority: "High"
      },
      {
        skill: "Machine Learning & AI",
        description: "Understanding of ML algorithms and AI implementation for competitive advantage",
        currentLevel: 45,
        targetLevel: 75,
        priority: "Medium"
      },
      {
        skill: "DevOps & CI/CD",
        description: "Advanced automation and deployment pipeline management",
        currentLevel: 70,
        targetLevel: 85,
        priority: "Medium"
      },
      {
        skill: "Data Analytics",
        description: "Advanced data analysis and visualization skills for data-driven decisions",
        currentLevel: 55,
        targetLevel: 80,
        priority: "Low"
      }
    ],
    jobRecommendations: [
      {
        id: 1,
        title: "Senior Software Engineer",
        company: "TechCorp Inc.",
        location: "San Francisco, CA",
        salary: "$120K - $150K",
        matchPercentage: 94,
        matchReason: "Your technical leadership skills and React expertise align perfectly with this role. The company values innovation and your problem-solving approach would be highly valued.",
        requiredSkills: [
          { name: "React", hasSkill: true },
          { name: "Node.js", hasSkill: true },
          { name: "AWS", hasSkill: false },
          { name: "TypeScript", hasSkill: true },
          { name: "GraphQL", hasSkill: false }
        ],
        isBookmarked: false
      },
      {
        id: 2,
        title: "Technical Lead",
        company: "Innovation Labs",
        location: "Remote",
        salary: "$140K - $170K",
        matchPercentage: 89,
        matchReason: "Your leadership experience and communication skills make you an ideal candidate for this technical lead position. The role involves mentoring junior developers.",
        requiredSkills: [
          { name: "Team Leadership", hasSkill: true },
          { name: "System Design", hasSkill: true },
          { name: "Microservices", hasSkill: false },
          { name: "Python", hasSkill: true },
          { name: "Docker", hasSkill: true }
        ],
        isBookmarked: true
      },
      {
        id: 3,
        title: "Full Stack Developer",
        company: "StartupXYZ",
        location: "Austin, TX",
        salary: "$100K - $130K",
        matchPercentage: 82,
        matchReason: "Your full-stack capabilities and startup experience align well with this fast-paced environment. The role offers significant growth opportunities.",
        requiredSkills: [
          { name: "React", hasSkill: true },
          { name: "Express.js", hasSkill: true },
          { name: "MongoDB", hasSkill: true },
          { name: "Redis", hasSkill: false },
          { name: "Kubernetes", hasSkill: false }
        ],
        isBookmarked: false
      }
    ],
    progressData: [
      { month: 'Jan', skillLevel: 65, marketDemand: 70 },
      { month: 'Feb', skillLevel: 68, marketDemand: 72 },
      { month: 'Mar', skillLevel: 72, marketDemand: 75 },
      { month: 'Apr', skillLevel: 75, marketDemand: 78 },
      { month: 'May', skillLevel: 78, marketDemand: 80 },
      { month: 'Jun', skillLevel: 82, marketDemand: 83 }
    ],
    salaryData: [
      { year: '2025', salary: 120 },
      { year: '2026', salary: 135 },
      { year: '2027', salary: 150 },
      { year: '2028', salary: 170 },
      { year: '2029', salary: 190 },
      { year: '2030', salary: 210 }
    ],
    actionPlan: {
      phases: [
        {
          title: "Immediate Actions (0-3 months)",
          duration: "Next 3 months",
          items: [
            {
              type: "certification",
              title: "AWS Solutions Architect Certification",
              description: "Gain cloud architecture expertise to fill your highest priority skill gap",
              estimatedTime: "40-60 hours",
              cost: "$150",
              priority: "High",
              link: "https://aws.amazon.com/certification/"
            },
            {
              type: "project",
              title: "Build a Cloud-Native Application",
              description: "Create a portfolio project demonstrating AWS services integration",
              estimatedTime: "20-30 hours",
              cost: "Free",
              priority: "High",
              link: null
            }
          ]
        },
        {
          title: "Short-term Goals (3-6 months)",
          duration: "3-6 months",
          items: [
            {
              type: "course",
              title: "Machine Learning Fundamentals",
              description: "Complete an online ML course to build foundational AI knowledge",
              estimatedTime: "60-80 hours",
              cost: "$49/month",
              priority: "Medium",
              link: "https://coursera.org"
            },
            {
              type: "networking",
              title: "Join Tech Leadership Communities",
              description: "Engage with senior engineering communities and attend virtual meetups",
              estimatedTime: "2-4 hours/week",
              cost: "Free",
              priority: "Medium",
              link: null
            }
          ]
        },
        {
          title: "Long-term Development (6-12 months)",
          duration: "6-12 months",
          items: [
            {
              type: "certification",
              title: "Advanced DevOps Certification",
              description: "Obtain advanced certification in CI/CD and infrastructure automation",
              estimatedTime: "80-100 hours",
              cost: "$200",
              priority: "Low",
              link: null
            },
            {
              type: "project",
              title: "Open Source Contribution",
              description: "Contribute to major open source projects to build reputation",
              estimatedTime: "5-10 hours/week",
              cost: "Free",
              priority: "Low",
              link: null
            }
          ]
        }
      ]
    }
  };

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'BarChart3',
      description: 'Career strengths and analysis'
    },
    {
      id: 'jobs',
      label: 'Job Matches',
      icon: 'Briefcase',
      description: 'Personalized recommendations'
    },
    {
      id: 'progress',
      label: 'Career Path',
      icon: 'TrendingUp',
      description: 'Progression and salary insights'
    },
    {
      id: 'action',
      label: 'Action Plan',
      icon: 'CheckSquare',
      description: 'Your development roadmap'
    },
    {
      id: 'export',
      label: 'Export',
      icon: 'Download',
      description: 'Download and share report'
    }
  ];

  useEffect(() => {
    // Initialize bookmarked jobs
    const initialBookmarks = new Set();
    reportData?.jobRecommendations?.forEach(job => {
      if (job?.isBookmarked) {
        initialBookmarks?.add(job?.id);
      }
    });
    setBookmarkedJobs(initialBookmarks);
  }, []);

  const handleJobApply = (jobId) => {
    console.log('Applying to job:', jobId);
    // Navigate to job application or external link
    navigate('/job-matching');
  };

  const handleJobBookmark = (jobId) => {
    const newBookmarks = new Set(bookmarkedJobs);
    if (newBookmarks?.has(jobId)) {
      newBookmarks?.delete(jobId);
    } else {
      newBookmarks?.add(jobId);
    }
    setBookmarkedJobs(newBookmarks);
  };

  const handleExportReport = async (format) => {
    console.log('Exporting report as:', format);
    // Simulate export process
    return new Promise(resolve => {
      setTimeout(() => {
        console.log('Report exported successfully');
        resolve();
      }, 2000);
    });
  };

  const handleShareReport = (platform) => {
    console.log('Sharing report on:', platform);
    if (platform === 'copy') {
      navigator.clipboard?.writeText(window.location?.href);
      alert('Report link copied to clipboard!');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CareerStrengthsCard strengths={reportData?.strengths} />
              <SkillGapAnalysis skillGaps={reportData?.skillGaps} />
            </div>
          </div>
        );

      case 'jobs':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">
                  {reportData?.jobRecommendations?.length} Job Recommendations
                </h3>
                <p className="text-sm text-text-secondary">
                  Curated positions based on your skills and career goals
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/job-matching')}
                iconName="Search"
                iconPosition="left"
              >
                Find More Jobs
              </Button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {reportData?.jobRecommendations?.map(job => (
                <JobRecommendationCard
                  key={job?.id}
                  job={{
                    ...job,
                    isBookmarked: bookmarkedJobs?.has(job?.id)
                  }}
                  onApply={handleJobApply}
                  onBookmark={handleJobBookmark}
                />
              ))}
            </div>
          </div>
        );

      case 'progress':
        return (
          <div className="space-y-6">
            <CareerProgressChart
              progressData={reportData?.progressData}
              salaryData={reportData?.salaryData}
            />
          </div>
        );

      case 'action':
        return (
          <div className="space-y-6">
            <ActionPlanCard actionPlan={reportData?.actionPlan} />
          </div>
        );

      case 'export':
        return (
          <div className="space-y-6">
            <ReportExportCard
              reportData={reportData}
              onExport={handleExportReport}
              onShare={handleShareReport}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/career-assessment')}
              >
                <Icon name="ArrowLeft" size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Career Report</h1>
                <p className="text-text-secondary">
                  Comprehensive analysis for {reportData?.user?.name} • Generated on {new Date(reportData.user.assessmentDate)?.toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Overall Score */}
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-text-primary mb-2">Overall Career Readiness</h2>
                  <p className="text-text-secondary">
                    Based on your skills assessment, experience, and career goals
                  </p>
                </div>
                <div className="text-center">
                  <div className="relative w-24 h-24">
                    <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="var(--color-muted)"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="var(--color-success)"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 40}`}
                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - reportData?.user?.overallScore / 100)}`}
                        className="spring-transition"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-success">{reportData?.user?.overallScore}%</span>
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary mt-2">Excellent</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap spring-transition ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mb-8">
            {renderTabContent()}
          </div>

          {/* Quick Actions */}
          <div className="bg-card rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="default"
                onClick={() => navigate('/job-matching')}
                iconName="Search"
                iconPosition="left"
                className="justify-center"
              >
                Find More Jobs
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/career-assessment')}
                iconName="RefreshCw"
                iconPosition="left"
                className="justify-center"
              >
                Retake Assessment
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                iconName="Home"
                iconPosition="left"
                className="justify-center"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CareerReport;